import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { DenialRiskResult, ActionableIssue } from './useDenialRiskAnalysis';

export type { DenialRiskResult, ActionableIssue };

interface OpNoteAuditPayload {
  operativeNote: string;
  billedCptCodes: Array<{ code: string; description: string; modifiers?: string[] }>;
  icd10Codes?: string[];
  patientDemographics?: Record<string, string>;
  procedureDescription?: string;
  implantLog?: string;
  fluoroscopyTime?: string;
}

export function useOpNoteRiskAnalysis() {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<DenialRiskResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const analyze = useCallback(async (payload: OpNoteAuditPayload): Promise<DenialRiskResult | null> => {
    setIsAnalyzing(true);
    setError(null);
    setResult(null);

    try {
      const body = {
        operative_note: payload.operativeNote,
        billed_cpt_codes: payload.billedCptCodes.map(c => ({
          code: c.code,
          description: c.description,
          modifiers: c.modifiers ?? [],
        })),
        icd_10_codes: payload.icd10Codes ?? [],
        modifiers_applied: payload.billedCptCodes
          .filter(c => c.modifiers && c.modifiers.length > 0)
          .map(c => ({ cpt_code: c.code, modifiers: c.modifiers })),
        patient_demographics: payload.patientDemographics ?? {},
        procedure_description: payload.procedureDescription ?? "",
        implant_log: payload.implantLog ?? null,
        fluoroscopy_time: payload.fluoroscopyTime ?? null,
      };

      const { data, error: fnError } = await supabase.functions.invoke('analyze-op-note-risk', {
        body,
      });

      if (fnError) {
        throw new Error(fnError.message || 'Edge function call failed');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      const analysisResult = data as DenialRiskResult;
      setResult(analysisResult);
      return analysisResult;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error during op note risk analysis';
      setError(msg);
      console.error('Op note risk analysis failed:', err);
      return null;
    } finally {
      setIsAnalyzing(false);
    }
  }, []);

  const clearResult = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return { isAnalyzing, result, error, analyze, clearResult };
}
